import { useDeferredMount } from "@/hooks/useDeferredMount";
import { Fragment, Suspense, lazy, useEffect, useRef, useState } from "react";
import type React from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Check, ChevronLeft, ChevronRight, MessageCircle, Route, Sparkles } from "lucide-react";
import calculatorImg from "@/assets/tools/calculator.webp";
import kpisImg from "@/assets/tools/kpis.webp";
import grantsImg from "@/assets/tools/grants.webp";
import suppliersImg from "@/assets/tools/suppliers.webp";
import premisesImg from "@/assets/tools/premises.webp";
import leaksImg from "@/assets/tools/leaks-hd.webp";
import postsImg from "@/assets/tools/posts-hd.webp";
import musicImg from "@/assets/tools/music-headphones-primary.png";
import scentImg from "@/assets/tools/scent-hd.webp";
import cafeteriaImg from "@/assets/go/cafeteria.webp";
import esteticaImg from "@/assets/go/estetica.webp";
import tatuajeImg from "@/assets/go/tatuaje.webp";
import barberiaImg from "@/assets/go/barberia.webp";
import modaImg from "@/assets/go/moda.webp";
import restauranteImg from "@/assets/go/restaurante.webp";
import libreriaImg from "@/assets/go/libreria.webp";
import { HomeCorporateFooter } from "@/components/Footer";
const PublicAgentConsult = lazy(() => import("@/components/agent/PublicAgentConsult"));
const moda = { url: modaImg }, restaurante = { url: restauranteImg }, cafeteria = { url: cafeteriaImg }, estetica = { url: esteticaImg }, barberia = { url: barberiaImg }, tatuaje = { url: tatuajeImg }, libreria = { url: libreriaImg };

const SECTORS = [
  { key: "moda", img: moda.url },
  { key: "restaurante", img: restaurante.url },
  { key: "cafeteria", img: cafeteria.url },
  { key: "estetica", img: estetica.url },
  { key: "barberia", img: barberia.url },
  { key: "tatuaje", img: tatuaje.url },
  { key: "libreria", img: libreria.url },
] as const;
import { useAuthState } from "@/hooks/useAuthState";
import { setPostAuthIntent } from "@/lib/post-auth-intent";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { GO_TRIAL_EVENT, GO_CLUB_EVENT } from "@/components/public/GoSidebar";

const FEATURES: Record<"es" | "en", Record<"basico" | "club_pro" | "completo", string[]>> = {
  es: {
    basico: ["30 consultas al Agente Retail IA", "Herramientas y diagnóstico", "Listado de subvenciones", "Música básica"],
    club_pro: ["60 consultas al Agente Retail IA", "Hoja de ruta completa y KPIs", "Subvenciones que encajan contigo", "Música 24/7 ilimitada"],
    completo: ["100 consultas al Agente Retail IA", "Todo lo de Club Pro", "Paneles de gestión avanzada", "Motores IA expertos: legal y fiscal"],
  },
  en: {
    basico: ["30 Retail AI Agent queries", "Tools and diagnosis", "Grants listing", "Basic music"],
    club_pro: ["60 Retail AI Agent queries", "Full roadmap and KPIs", "Grants that fit you", "Unlimited 24/7 music"],
    completo: ["100 Retail AI Agent queries", "Everything in Club Pro", "Advanced management dashboards", "Expert AI engines: legal and tax"],
  },
};


const TOOL_TILES = [
  { key: "leaks", img: leaksImg, es: "/detector-de-fugas", en: "/en/leak-detector", free: true },
  { key: "calculator", img: calculatorImg, es: "/herramientas/calculadora", en: "/en/tools/calculator", free: true },
  { key: "grants", img: grantsImg, es: "/recursos/subvenciones", en: "/en/resources/subsidies", free: true },
  { key: "kpis", img: kpisImg, es: "/herramientas/kpis", en: "/en/tools/kpis" },
  { key: "premises", img: premisesImg, es: "/herramientas/mis-locales", en: "/en/tools/my-premises" },
  { key: "suppliers", img: suppliersImg, es: "/herramientas/proveedores", en: "/en/tools/suppliers" },
  { key: "posts", img: postsImg, es: "/publicaciones-para-tu-local", en: "/en/social-posts-for-your-store" },
  { key: "music", img: musicImg, es: "/musica-para-tu-local", en: "/en/music-for-your-store" },
  { key: "scent", img: scentImg, es: "/aroma-para-tu-local", en: "/en/scent-for-your-store" },
] as const;
type ToolKey = (typeof TOOL_TILES)[number]["key"];

const EXTRA: Record<"es" | "en", { sectorsLine: string; whisper: string; moreQ: string; moreCta: string; roadPill: string; roadTitle: string; roadBody: string; roadSteps: [string, string][]; toolsTitle: string; toolsSub: string; agentTile: string; tools: Record<ToolKey, [string, string]>; casesTitle: string; casesSub: string; cases: [string, string, string][]; readMore: string }> = {
  es: {
    sectorsLine: "Moda, hostelería, cafeterías, estética, barberías, librerías, tatuaje y mucho más. Entiende tu sector y te dice qué priorizar cada semana, mientras tú atiendes a tus clientes.",
    whisper: "«Antes de pedir más género, mira qué se está quedando en el almacén.»",
    moreQ: "¿Quieres hacerle tu propia pregunta? Regístrate y pruébalo 7 días gratis.",
    moreCta: "Preguntar al Agente",
    roadPill: "EL MÉTODO DETRÁS DEL AGENTE",
    roadTitle: "Cada respuesta se apoya en tu hoja de ruta.",
    roadBody: "El Agente Retail IA no responde al aire: trabaja sobre una hoja de ruta adaptada a tu sector, que ordena lo que toca hacer y en qué orden.",
    roadSteps: [["Dónde estás", "Un diagnóstico rápido del momento de tu negocio."], ["Qué toca primero", "Prioridades claras por áreas: clientes, equipo, precios, local."], ["Pasos que puedes hacer", "Tareas concretas que entran en tu semana."], ["El Agente te acompaña", "Le preguntas sobre cada paso cuando lo necesitas."]],
    toolsTitle: "Herramientas del Club Pro",
    toolsSub: "Descubre cuáles puedes probar. Todas conectadas al Agente Retail IA.",
    agentTile: "Tu experto de cabecera: pregúntale lo que necesites de tu negocio.",
    tools: { leaks: ["Detector de fugas", "Dónde mejorar"], calculator: ["Calculadora", "Cómo aguanta tu negocio"], grants: ["Subvenciones", "Ayudas activas"], kpis: ["KPIs", "Lo esencial, con histórico"], premises: ["Mis locales", "La ficha de cada puerta"], suppliers: ["Proveedores", "Compara presupuestos"], posts: ["Publicaciones", "Ideas para redes"], music: ["Música", "Legal e ilimitada"], scent: ["Aroma", "Identidad olfativa"] },
    casesTitle: "Consultas resueltas",
    casesSub: "Dudas reales de comercios a pie de calle, respondidas por el Agente Retail IA.",
    cases: [["Tienda de ropa", "¿Cómo atraigo más clientes?", "Escaparate, base de clientes y motivos para volver, ordenados por lo que puedes hacer esta semana."], ["Hostelería", "¿Cómo calculo el coste real de un plato?", "Escandallo con merma, coste por ración y comparación con el precio de carta."], ["Cualquier comercio", "¿Qué ayudas puedo pedir para mi local?", "Tipos de ayudas habituales, dónde mirarlas y qué preparar antes de solicitar."]],
    readMore: "Ver consultas",
  },
  en: {
    sectorsLine: "Fashion, hospitality, cafés, beauty, barbers, bookshops, tattoo and more. It understands your sector and tells you what to prioritise each week while you serve your customers.",
    whisper: "“Before ordering more stock, check what's sitting in the storeroom.”",
    moreQ: "Want to ask your own question? Sign up and try it free for 7 days.",
    moreCta: "Ask the Agent",
    roadPill: "THE METHOD BEHIND THE AGENT",
    roadTitle: "Every answer builds on your roadmap.",
    roadBody: "The Retail AI Agent doesn't answer in a vacuum: it works on a roadmap adapted to your sector that orders what to do and when.",
    roadSteps: [["Where you are", "A quick diagnosis of your business moment."], ["What comes first", "Clear priorities by area: customers, team, prices, premises."], ["Steps you can do", "Concrete tasks that fit your week."], ["The Agent by your side", "Ask about each step whenever you need."]],
    toolsTitle: "Club Pro tools",
    toolsSub: "Practical tools for your shop, all connected to the Retail AI Agent.",
    agentTile: "Your go-to retail expert for the questions your business needs answered.",
    tools: { leaks: ["Leak detector", "Where to improve"], calculator: ["Calculator", "How your business holds up"], grants: ["Grants in Spain", "Open public grants"], kpis: ["KPIs", "The essentials, with history"], premises: ["My locations", "A profile for each location"], suppliers: ["Suppliers", "Compare quotes"], posts: ["Social posts", "Ideas for social"], music: ["Music", "Licensed, unlimited music"], scent: ["Scent", "Your shop's signature scent"] },
    casesTitle: "Answered questions",
    casesSub: "Real questions from high-street shops, answered by the Retail AI Agent.",
    cases: [["Clothing shop", "How do I attract more customers?", "Window, customer base and reasons to come back, ordered by what you can do this week."], ["Hospitality", "How do I work out the real cost of a dish?", "Costing with waste, cost per portion and comparison with the menu price."], ["Any shop", "What grants can I apply for?", "Common grant types, where to look and what to prepare before applying."]],
    readMore: "See answers",
  },
};

function useIsLg() {
  const [lg, setLg] = useState(true);
  useEffect(() => {
    const m = window.matchMedia("(min-width: 1024px)");
    const on = () => setLg(m.matches);
    on(); m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return lg;
}

/** Ordenador/iPad: comercio a la izquierda y hostelería/servicios a la derecha, rotando a la vez cada 5 s. */
const SPLIT_LEFT = [0, 6]; // moda, librería
const SPLIT_RIGHT = [2, 1]; // cafetería, restaurante
function HeroSplit({ agent, labels, whispers }: { agent: string; labels: readonly string[]; whispers: readonly string[] }) {
  // Escena: fotos fijas → pausa sin globos → globo izquierdo → globo derecho → cambian las dos fotos y se repite.
  const [t, setT] = useState(0);
  const [bubble, setBubble] = useState(-1);
  const [jump, setJump] = useState(0);
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    let alive = true;
    const run = () => {
      if (!alive) return;
      setBubble(-1);
      timers.push(setTimeout(() => setBubble(0), 2500));
      timers.push(setTimeout(() => setBubble(1), 7000));
      timers.push(setTimeout(() => setBubble(-1), 11500));
      timers.push(setTimeout(() => { setT((v) => v + 1); run(); }, 12300));
    };
    run();
    return () => { alive = false; timers.forEach(clearTimeout); };
  }, [jump]);
  const scenes = Math.max(SPLIT_LEFT.length, SPLIT_RIGHT.length);
  const sides = [
    { list: SPLIT_LEFT, align: "left-[3%]" },
    { list: SPLIT_RIGHT, align: "right-[3%]" },
  ];
  const fade = "linear-gradient(to bottom, black 0%, black 52%, rgb(0 0 0 / 0.85) 64%, rgb(0 0 0 / 0.5) 76%, rgb(0 0 0 / 0.18) 88%, transparent 100%)";
  return (
    <div className="relative hidden h-[100svh] min-h-[680px] grid-cols-2 gap-1.5 bg-card md:grid">
      {sides.map(({ list, align }, side) => {
        const cur = list[t % list.length] ?? 0;
        const m = cur;
        const on = bubble === side;
        return (
          <div key={side} className="relative overflow-hidden bg-card">
            <div className="absolute inset-0" style={{ WebkitMaskImage: fade, maskImage: fade }}>
            {list.map((k) => (
              <img key={k} src={SECTORS[k]?.img} alt={`Retail Lemon · ${agent} · ${labels[k]}`} width={784} height={1168} fetchPriority={k === list[0] ? "high" : "low"} loading={k === list[0] ? "eager" : "lazy"} className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${k === cur ? "opacity-100" : "opacity-0"}`} />
            ))}
            </div>
            <div aria-live="polite" aria-hidden={!on} className={`group-data-[started=true]/hero:hidden absolute top-[calc(100%-var(--hero-card)-7rem)] xl:top-[calc(100%-var(--hero-card))] z-20 flex w-[min(20rem,88%)] xl:w-[clamp(12rem,calc(97%-22.5rem),20rem)] items-start gap-2 rounded-2xl border border-border/60 bg-card/95 px-3 py-2 text-foreground shadow-lg backdrop-blur-md transition-all duration-500 ${on ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0"} ${align}`}>
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary"><MessageCircle className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={1.5} /></span>
              <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{agent} · {labels[m]}</p><p className="text-[13px] font-medium leading-snug text-pretty">{whispers[m]}</p></div>
            </div>
          </div>
        );
      })}
      <div className="absolute bottom-5 right-6 z-30 flex items-center">
        {Array.from({ length: scenes }, (_, k) => {
          const act = t % scenes === k;
          return (
            <button key={k} type="button" aria-label={`${k + 1} / ${scenes}`} aria-current={act} onClick={() => { setT(k); setJump((j) => j + 1); }} className="flex h-6 items-center px-1">
              <span className={`block h-1.5 rounded-full transition-all duration-500 ${act ? "w-4 bg-foreground/60" : "w-1.5 bg-foreground/20 hover:bg-foreground/40"}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HeroPhoto({ agent, labels, whispers, es, bleed = false }: { agent: string; labels: readonly string[]; whispers: readonly string[]; es: boolean; bleed?: boolean }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = SECTORS.length;
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((v) => (v + 1) % n), 5000);
    return () => clearInterval(t);
  }, [paused, n]);
  const go = (d: number) => setI((v) => (v + d + n) % n);
  const tx = useRef<number | null>(null);
  return (
    <div className={bleed ? "relative overflow-hidden bg-muted" : "relative overflow-hidden rounded-[2rem] border border-border bg-muted shadow-2xl"} onTouchStart={(e) => { setPaused(true); tx.current = e.touches[0]?.clientX ?? null; }} onTouchEnd={(e) => { if (tx.current == null) return; const d = (e.changedTouches[0]?.clientX ?? tx.current) - tx.current; tx.current = null; if (Math.abs(d) > 40) go(d < 0 ? 1 : -1); }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className={bleed ? "relative aspect-[4/5] w-full md:aspect-auto md:h-[calc(100svh-61px)] md:min-h-[640px]" : "relative aspect-[4/5] w-full"}>
        {SECTORS.map((s, k) => (
          <img key={s.key} src={s.img} alt={`Retail Lemon · ${agent} · ${labels[k]}`} width={784} height={1168} fetchPriority={k === 0 ? "high" : "low"} loading={k === 0 ? "eager" : "lazy"} className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${k === i ? "opacity-100" : "opacity-0"}`} />
        ))}
      </div>
      {bleed && <div className="absolute inset-x-4 bottom-[13%] z-20 md:bottom-[calc(var(--hero-card)_+_0.75rem)] flex flex-col items-center gap-0.5">
        <div key={i} aria-live="polite" className="flex w-full max-w-sm items-start gap-2.5 rounded-2xl border border-background/25 bg-foreground/45 px-3 py-2 text-background shadow-lg backdrop-blur-md animate-in fade-in duration-500">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary"><MessageCircle className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={1.5} /></span>
          <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-wide text-background/80">{agent} · {labels[i]}</p><p className="text-[13px] font-medium leading-snug">{whispers[i]}</p></div>
        </div>
        <div className="flex justify-center gap-0.5">
          {SECTORS.map((s, k) => (
            <button key={s.key} type="button" onClick={() => setI(k)} aria-label={labels[k]} className="flex h-1.5 items-center px-[3px]"><span className={`block h-[3px] rounded-full transition-all ${k === i ? "w-[7px] bg-background" : "w-[3px] bg-background/40"}`} /></button>
          ))}
        </div>
      </div>}
      {!bleed && <button type="button" onClick={() => go(-1)} aria-label={es ? "Anterior" : "Previous"} className={`absolute left-3 ${bleed ? "top-[42%]" : "top-1/2"} flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/90 shadow-md hover:bg-card`}><ChevronLeft className="h-5 w-5" strokeWidth={1.5} /></button>}
      {!bleed && <button type="button" onClick={() => go(1)} aria-label={es ? "Siguiente" : "Next"} className={`absolute right-3 ${bleed ? "top-[42%]" : "top-1/2"} flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/90 shadow-md hover:bg-card`}><ChevronRight className="h-5 w-5" strokeWidth={1.5} /></button>}
      {!bleed && <div className="absolute inset-x-4 bottom-4 rounded-2xl bg-card/95 px-4 py-3 shadow-lg backdrop-blur">
        <div className="flex items-start gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary"><MessageCircle className="h-4 w-4 text-primary-foreground" strokeWidth={1.5} /></span>
          <div><p className="text-[11px] font-semibold text-muted-foreground">{agent} · {labels[i]}</p><p className="text-sm font-medium">{whispers[i]}</p></div>
        </div>
        <div className="mt-3 flex justify-center gap-1.5">
          {SECTORS.map((s, k) => (
            <button key={s.key} type="button" onClick={() => setI(k)} aria-label={labels[k]} className="flex h-6 items-center px-0.5"><span className={`block h-1.5 rounded-full transition-all ${k === i ? "w-5 bg-foreground" : "w-1.5 bg-foreground/25"}`} /></button>
          ))}
        </div>
      </div>}
    </div>
  );
}

const TOOL_BACK: Record<"es" | "en", Record<ToolKey, string>> = {
  es: {
    leaks: "Antes de que se te escape más dinero: detecta en minutos dónde pierdes margen cada mes.",
    calculator: "Antes de firmar un alquiler o cualquier compromiso, comprueba cómo aguanta tu negocio.",
    grants: "Ayudas públicas activas para tu comercio, con requisitos y plazos en un solo sitio.",
    kpis: "Los números esenciales de tu negocio, con histórico para ver si vas mejor o peor.",
    premises: "La ficha de cada local: alquiler, superficie y datos clave siempre a mano.",
    suppliers: "Compara presupuestos de proveedores antes de decidir.",
    posts: "Ideas de publicaciones para redes adaptadas a tu negocio.",
    music: "Música legal para tu local, sin preocuparte de licencias.",
    scent: "Crea una identidad olfativa para que tu local se recuerde.",
  },
  en: {
    leaks: "Before more money slips away: find in minutes where you lose margin every month.",
    calculator: "Before signing a lease or any commitment, check how your business holds up.",
    grants: "Open public grants for your shop, with requirements and deadlines in one place.",
    kpis: "Your business essentials, with history to see whether you're doing better or worse.",
    premises: "Each premises' profile: rent, size and key data always at hand.",
    suppliers: "Compare supplier quotes before you decide.",
    posts: "Social post ideas adapted to your business.",
    music: "Legal music for your premises, without licence worries.",
    scent: "Create a scent identity so your shop is remembered.",
  },
};

/** Tira sin fin: avanza sola, se para al tocar/pasar el ratón y se arrastra a mano en ambos sentidos. */
function InfiniteRail({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const third = () => el.scrollWidth / 3;
    el.scrollLeft = third();
    let paused = false;
    let resume: ReturnType<typeof setTimeout> | undefined;
    let pos = el.scrollLeft;
    const onScroll = () => {
      const t = third();
      if (el.scrollLeft < t * 0.5) el.scrollLeft += t;
      else if (el.scrollLeft > t * 1.5) el.scrollLeft -= t;
      if (paused) pos = el.scrollLeft;
    };
    const pause = () => { paused = true; if (resume) clearTimeout(resume); };
    const play = (delay = 1500) => { if (resume) clearTimeout(resume); resume = setTimeout(() => { pos = el.scrollLeft; paused = false; }, delay); };
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = now - last; last = now;
      if (!paused && !reduce) { pos += dt * 0.035; el.scrollLeft = pos; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const enter = () => pause();
    const leave = () => play(300);
    const tstart = () => pause();
    const tend = () => play(2000);
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("mouseenter", enter);
    el.addEventListener("mouseleave", leave);
    el.addEventListener("touchstart", tstart, { passive: true });
    el.addEventListener("touchend", tend);
    el.addEventListener("wheel", pause, { passive: true });
    return () => {
      cancelAnimationFrame(raf); if (resume) clearTimeout(resume);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("mouseenter", enter);
      el.removeEventListener("mouseleave", leave);
      el.removeEventListener("touchstart", tstart);
      el.removeEventListener("touchend", tend);
      el.removeEventListener("wheel", pause);
    };
  }, []);
  return (
    <div ref={ref} className="mx-[calc(50%-50cqw)] mt-8 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div className="flex w-max items-stretch gap-3">{children}</div>
    </div>
  );
}

function ToolFlip({ img, name, hint, back, href, es, free }: { img: string; name: string; hint: string; back: string; href: string; es: boolean; free: boolean }) {
  const [f, setF] = useState(false);
  return (
    <div className="h-48 [perspective:1000px] sm:h-56">
      <div role="button" tabIndex={0} aria-label={name} aria-pressed={f} onClick={() => setF((v) => !v)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setF((v) => !v); } }}
        style={{ transform: `rotateY(${f ? 180 : 0}deg)`, transition: "transform 900ms cubic-bezier(0.45,0,0.25,1)", transformStyle: "preserve-3d" }}
        className="relative h-full w-full cursor-pointer select-none rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-card p-3 text-center shadow-sm [backface-visibility:hidden]">
          <img src={img} alt={`Retail Lemon · ${name}`} loading="lazy" draggable={false} className="h-20 w-20 object-contain sm:h-24 sm:w-24" />
          <span className="font-display text-sm font-bold leading-tight sm:text-base">{name}</span>
          <span className="text-[11px] text-muted-foreground">{hint}</span>
        </div>
        <div className="absolute inset-0 flex flex-col rounded-2xl border border-border bg-card p-3 text-left shadow-sm [backface-visibility:hidden] [transform:rotateY(180deg)] sm:p-4">
          <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{name}</span>
          <p className="mt-2 flex-1 overflow-y-auto text-xs leading-snug sm:text-sm">{back}</p>
          <a href={href} onClick={(e) => e.stopPropagation()} className="mt-2 flex min-h-[40px] items-center justify-center gap-1 rounded-full bg-foreground text-xs font-bold text-background sm:text-sm">{es ? "Ver herramienta" : "View tool"}<ArrowRight className="h-4 w-4" /></a>
        </div>
      </div>
    </div>
  );
}

const WHY = {
  es: {
    title: "Por qué el Agente Retail IA",
    items: [
      "Respuestas en segundos, adaptadas a tu sector",
      "Te dice qué priorizar cada semana",
      "Pasos concretos que caben en tu día a día",
      "Conectado a herramientas (KPIs, subvenciones, calculadora…)",
    ],
  },
  en: {
    title: "Why the Retail AI Agent",
    items: [
      "Answers in seconds, adapted to your sector",
      "Tells you what to prioritise each week",
      "Concrete steps that fit your day to day",
      "Connected to your tools (KPIs, grants, calculator…)",
    ],
  },
} as const;

/** Lista de motivos con checks: sustituye al carrusel de tarjetas. */
function WhyAgent({ es }: { es: boolean }) {
  const w = WHY[es ? "es" : "en"];
  return (
    <div className="mx-auto mt-12 max-w-2xl">
      <h2 className="font-display text-center text-2xl font-extrabold sm:text-3xl">{w.title}</h2>
      <ul className="mt-8 space-y-4">
        {w.items.map((t) => (
          <li key={t} className="flex items-start gap-3 text-[15px] leading-snug sm:text-base">
            <span className="mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary">
              <Check className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={3} />
            </span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const TICKER = {
  es: ["Tienda de Ropa", "Zapatería", "Restaurante", "Bar", "Cafetería", "Panadería", "Peluquería", "Centro de Estética", "Farmacia", "Parafarmacia", "Supermercado", "Tienda de Alimentación", "Muebles y Decoración", "Joyería", "Inmobiliaria"],
  en: ["Clothing Store", "Shoe Store", "Restaurant", "Bar", "Café", "Bakery", "Hair Salon", "Beauty Salon", "Pharmacy", "Health & Beauty Store", "Supermarket", "Grocery Store", "Furniture & Decor", "Jewelry Store", "Real Estate Agency"],
};

function SectorTicker({ lang }: { lang: "es" | "en" }) {
  const items = [...TICKER[lang], ...TICKER[lang]];
  return (
    <div className="-mx-4 overflow-hidden border-y border-border bg-card py-3" aria-label={lang === "es" ? "Sectores" : "Sectors"}>
      <div className="flex w-max animate-[go-ticker_40s_linear_infinite] gap-8 motion-reduce:animate-none">
        {items.map((t, i) => (
          <span key={i} aria-hidden={i >= TICKER[lang].length} className="flex items-center gap-8 whitespace-nowrap text-sm font-semibold text-muted-foreground">{t}<span className="h-1.5 w-1.5 rounded-full bg-primary" /></span>
        ))}
      </div>
      <style>{"@keyframes go-ticker{from{transform:translateX(0)}to{transform:translateX(-50%)}}"}</style>
    </div>
  );
}

const LazyAuthModal = lazy(() => import("@/components/LazyAuthModal"));
const GoogleOneTap = lazy(() => import("@/components/GoogleOneTap"));

type Lang = "es" | "en";
type PlanKey = "basico" | "club_pro" | "completo";

const COPY = {
  es: {
    notice: "7 días de prueba gratis · Sin permanencia · Cancela en 1 clic",
    trial: "Seleccionar",
    choose: "Elegir plan",
    trialNote: "Todos los planes incluyen 7 días de prueba gratis.",
    pill: "AGENTE RETAIL IA · CLUB PRO",
    h1: "El experto que te acompaña en tu negocio a pie de calle.",
    grantsPill: "Además: no te dejes dinero público sobre la mesa. Subvenciones para tu comercio.",
    sectors: ["Tienda de ropa", "Restaurante", "Cafetería", "Centro de estética", "Barbería", "Estudio de tatuaje", "Librería"],
    whispers: [
      "«Antes del escaparate, revisa surtido y precios de esta temporada.»",
      "«Ese plato nuevo: calcula su coste antes de ponerlo en carta.»",
      "«Mira si hay alguna ayuda abierta para renovar tu maquinaria.»",
      "«Ordena las citas de la semana para no tener huecos muertos.»",
      "«Hoy toca revisar qué servicios te dejan más margen.»",
      "«Antes de reformar el estudio, mira qué subvenciones encajan contigo.»",
      "«Coloca las novedades a la altura de la vista y revisa lo que no rota.»",
    ],
    grantsTitle: "¿Sabes qué subvenciones puede pedir tu negocio?",
    grantsCta: "Ver planes",
    grantsBody: "Hay ayudas públicas para digitalizar, reformar el local, contratar o mejorar la eficiencia. Retail Lemon te las reúne en un listado claro y, desde Club Pro, te muestra las que encajan con tu negocio.",
    grantsPoints: ["Listado de subvenciones desde el plan Básico", "Subvenciones que encajan contigo en Club Pro y Avanzado", "Requisitos y plazos en un solo sitio"],
    grantsNote: "Una sola ayuda bien aprovechada puede cubrir con creces lo que pagas por el servicio.",
    sub: "El Agente Retail IA, una hoja de ruta adaptada a tu sector y herramientas prácticas para decidir, organizar y mejorar los detalles que mueven tu negocio.",
    agentName: "Agente Retail IA",
    sector: "Moda · Apertura",
    mockQ: "Voy a abrir una tienda de moda en dos meses. ¿Qué debo dejar resuelto antes de inaugurar?",
    mockA: "Vamos por orden. Antes de pensar en el escaparate, cierra lo que condiciona todo lo demás: el local y la zona, el surtido con sus precios, quién atiende y cómo será la operación del primer mes.",
    typing: "Escribiendo…",
    online: "en línea",
    roadmapTitle: "TU HOJA DE RUTA",
    roadmap: ["Local y zona", "Surtido y precios", "Equipo y horarios", "Plan de apertura"],
    today: "Hoy",
    vsTitle: "No es un programa para llevar la caja. Ni un informe que se queda en un cajón.",
    vsSub: "Es una forma más directa de tener método, criterio y herramientas para gestionar mejor tu negocio.",
    whatTitle: "Qué incluye",
    what: [
      ["Agente Retail IA", "Respuestas directas a dudas concretas de operación, apertura, clientes, equipo y precios."],
      ["Hoja de ruta por sector", "Un método que convierte objetivos grandes en prioridades claras y pasos que puedes hacer."],
      ["Herramientas que aterrizan decisiones", "Viabilidad, local, costes, alquiler y zona, para revisar antes de que se compliquen."],
      ["Música para tu local", "Música para tu tienda según lo incluido en cada plan."],
    ],
    howTitle: "Pregunta al Agente Retail IA",
    quick: [
      ["Voy a abrir una tienda de moda. ¿Por dónde empiezo?", ["Primero, valida local y zona: paso de gente, competencia y un alquiler que puedas sostener.", "Después, surtido y precios: pocas familias, bien definidas, con margen calculado.", "Tercero, equipo y horarios del primer mes.", "Lo marco en tu hoja de ruta: «Local y zona» para hoy."]],
      ["¿Cómo calculo el coste de un plato nuevo?", ["Haz el escandallo: cada ingrediente por su peso real, incluida la merma.", "Suma la parte de energía y desechables por ración.", "Compara el coste con el precio de carta y mira si encaja con tu margen objetivo.", "Puedes hacerlo con la calculadora del panel en unos minutos."]],
      ["¿Hay ayudas para renovar mi local?", ["Hay líneas públicas para reforma, digitalización y eficiencia, según tu comunidad autónoma.", "Te enseño el listado con requisitos y plazos en un solo sitio.", "Desde Club Pro, filtro las que encajan con tu negocio.", "La solicitud la presentas tú ante el organismo oficial."]],
    ],
    examples: [
      ["¿Qué debería revisar primero esta semana?", "Ordena tus prioridades según el momento de tu negocio y tu sector, para que no te disperses."],
      ["Voy a abrir: ¿qué no puedo dejar para el último momento?", "Te ayuda a estructurar los pasos de apertura y a ver qué depende de qué."],
      ["¿Dónde puedo mejorar sin complicar más el día a día?", "Convierte una preocupación general en acciones concretas que entran en tu semana."],
    ],
    pricesTitle: "Elige tu plan",
    month: "/mes",
    popular: "EL MÁS POPULAR",
    plans: { basico: "Para poner orden en tu negocio", club_pro: "Para tomar mejores decisiones cada semana", completo: "Para optimizar y hacer crecer el negocio" },
    names: { basico: "Básico", club_pro: "Club Pro", completo: "Avanzado" },
    faqTitle: "Preguntas frecuentes",
    faq: [
      ["¿Cómo funciona la prueba?", "Eliges tu plan y lo pruebas 7 días gratis. Después sigue como suscripción mensual; si no te convence, cancelas antes."],
      ["¿Puedo cancelar cuando quiera?", "Sí. Sin permanencia: cancelas desde tu perfil en 1 clic."],
      ["¿Sirve para mi sector?", "Está pensado para negocios a pie de calle: moda, hostelería, alimentación, belleza, servicios y comercio en general."],
      ["¿Me ayuda con las subvenciones?", "Sí. Desde Básico tienes el listado de subvenciones para comercio; en Club Pro y Avanzado, además, las que encajan con tu negocio. La solicitud la haces tú en el organismo oficial."],
      ["¿Qué pasa con la música?", "Cada plan incluye un acceso distinto a la música para tu local. Puedes verlo en el detalle de cada plan."],
    ],
    finalTitle: "Tu negocio puede funcionar con más claridad desde hoy.",
  },
  en: {
    notice: "7-day free trial · No commitment · Cancel in 1 click",
    trial: "Select",
    choose: "Choose plan",
    trialNote: "Every plan includes a 7-day free trial.",
    pill: "RETAIL AI AGENT · CLUB PRO",
    h1: "The retail expert by your side, every day.",
    grantsPill: "In Spain: don't leave public money on the table. Find grants for your shop.",
    sectors: ["Clothing shop", "Restaurant", "Café", "Beauty salon", "Barber shop", "Tattoo studio", "Bookshop"],
    whispers: [
      "“Before the window display, review this season's range and prices.”",
      "“That new dish: work out its cost before it goes on the menu.”",
      "“Check whether there's an open grant to renew your equipment.”",
      "“Order this week's bookings so there are no dead gaps.”",
      "“Today: check which services leave you the best margin.”",
      "“Before refurbishing the studio, see which grants fit you.”",
      "“Put new titles at eye level and review what isn’t moving.”",
    ],
    grantsTitle: "Running a business in Spain? See which grants you could claim.",
    grantsCta: "See plans",
    grantsBody: "For businesses in Spain, Retail Lemon gathers public grants to digitalise, refurbish your premises, hire or improve efficiency, and from Club Pro shows the ones that fit you. Opening in another country? The Agent, roadmap and tools still help you plan; grants and local procedures are currently covered for Spain only.",
    grantsPoints: ["Spanish grants listing from the Basic plan", "Grants that fit you on Club Pro and Advanced", "Requirements and deadlines in one place"],
    grantsNote: "A single well-used grant can more than cover what you pay for the service.",
    sub: "The Retail AI Agent, a roadmap adapted to your sector and practical tools to decide, organise and improve the details that move your business.",
    agentName: "Retail AI Agent",
    sector: "Fashion · Opening",
    mockQ: "I'm opening a fashion shop in two months. What should I have sorted before launch?",
    mockA: "Let's go step by step. Before the window display, settle what shapes everything else: premises and area, the range and its prices, who serves customers and how the first month will run.",
    typing: "Typing…",
    online: "online",
    roadmapTitle: "YOUR ROADMAP",
    roadmap: ["Premises and area", "Range and prices", "Team and hours", "Opening plan"],
    today: "Today",
    vsTitle: "It's not a till program. Nor a report that ends up in a drawer.",
    vsSub: "It's a more direct way to have a method, judgement and tools to run your business better.",
    whatTitle: "What's included",
    what: [
      ["Retail AI Agent", "Direct answers to concrete questions on operations, opening, customers, staff and pricing."],
      ["Roadmap by sector", "A method that turns big goals into clear priorities and doable steps."],
      ["Tools to make confident decisions", "Check viability, premises, costs, rent and area before things get complicated."],
      ["Music for your shop", "Music for your shop according to what each plan includes."],
    ],
    howTitle: "Ask the Retail AI Agent",
    quick: [
      ["I'm opening a fashion shop. Where do I start?", ["First, validate premises and area: footfall, competition and a rent you can sustain.", "Then range and prices: few, well-defined families with a worked-out margin.", "Third, team and hours for the first month.", "I'm adding it to your roadmap: “Premises and area” for today."]],
      ["How do I cost a new dish?", ["Cost each ingredient by its real weight, including waste.", "Add the share of energy and disposables per portion.", "Compare the cost with the menu price and check it fits your target margin.", "You can do it with the panel calculator in minutes."]],
      ["Are there grants to refurbish my premises?", ["There are public lines for refurbishment, digitalisation and efficiency, depending on your region.", "I show you the listing with requirements and deadlines in one place.", "From Club Pro, I filter the ones that fit your business.", "You submit the application yourself with the official body."]],
    ],
    examples: [
      ["What should I check first this week?", "It orders your priorities by your business moment and sector, so you stay focused."],
      ["I'm opening: what can't I leave until the last minute?", "It helps you structure the opening steps and see what depends on what."],
      ["Where can I improve without making my day harder?", "It turns a general worry into concrete actions that fit your week."],
    ],
    pricesTitle: "Choose your plan",
    month: "/mo",
    popular: "MOST POPULAR",
    plans: { basico: "To bring order to your business", club_pro: "To make better decisions every week", completo: "To optimise and grow the business" },
    names: { basico: "Basic", club_pro: "Club Pro", completo: "Advanced" },
    faqTitle: "Frequently asked questions",
    faq: [
      ["How does the trial work?", "Pick your plan and try it free for 7 days. Then it continues monthly; if it's not for you, cancel before."],
      ["Can I cancel anytime?", "Yes. No commitment: cancel from your profile in 1 click."],
      ["Is it for my sector?", "It's built for local businesses: fashion, hospitality, food, beauty, services and retail in general."],
      ["Does it work outside Spain?", "Yes, the Retail AI Agent, roadmap and tools help you plan and run your business anywhere. Grants, public procedures and some local references are currently available for Spain only."],
      ["Does it help with grants?", "Yes, for businesses in Spain. From Basic you get the grants listing; on Club Pro and Advanced, also the ones that fit your business. You apply yourself with the official body."],
      ["What about music?", "Each plan includes different access to music for your shop. See each plan's details."],
    ],
    finalTitle: "Run your business with more clarity, starting today.",
  },
} as const;

const PRICES: Record<PlanKey, number> = { basico: 39, club_pro: 69, completo: 99 };
/** Annual = 10 monthly payments (2 months free), shown as monthly equivalent. */
export const annualMonthly = (monthly: number, lang: Lang) =>
  ((monthly * 10) / 12).toLocaleString(lang === "es" ? "es-ES" : "en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function LiveChat({ c }: { c: (typeof COPY)[Lang] }) {
  const [q, setQ] = useState(0);
  const [shown, setShown] = useState(0);
  const [typing, setTyping] = useState(true);
  const lines = c.quick[q]?.[1] ?? [];
  useEffect(() => {
    setShown(0);
    setTyping(true);
    const timers: ReturnType<typeof setTimeout>[] = [];
    lines.forEach((_, i) => timers.push(setTimeout(() => { setTyping(false); setShown(i + 1); }, 700 + i * 650)));
    return () => timers.forEach(clearTimeout);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="w-full rounded-[2rem] border border-border bg-card p-5 shadow-2xl sm:p-6">
      <div className="flex items-center gap-3 border-b border-border pb-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary"><MessageCircle className="h-5 w-5 text-primary-foreground" strokeWidth={1.5} /></span>
        <span className="font-display text-base font-bold">{c.agentName}</span>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground"><span className="h-2 w-2 rounded-full bg-success animate-pulse" />{c.online}</span>
      </div>
      <div className="flex flex-wrap gap-2 pt-4">
        {c.quick.map(([label], i) => (
          <button key={label} type="button" onClick={() => setQ(i)} className={`min-h-[44px] rounded-full border px-4 py-2 text-left text-sm font-semibold transition-colors ${q === i ? "border-foreground bg-foreground text-background" : "border-border bg-background hover:bg-muted"}`}>{label}</button>
        ))}
      </div>
      <div key={q} className="mt-4 min-h-[260px] space-y-3 text-sm">
        <p className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-muted px-4 py-3 animate-in fade-in duration-300">{c.quick[q]?.[0]}</p>
        {typing && <p className="max-w-[40%] rounded-2xl rounded-bl-sm border border-border bg-background px-4 py-3 text-muted-foreground animate-pulse">{c.typing}</p>}
        {shown > 0 && (
          <div className="max-w-[95%] rounded-2xl rounded-bl-sm border border-border bg-background px-4 py-3">
            <ol className="space-y-2">
              {lines.slice(0, shown).map((l, i) => (
                <li key={l} className="flex gap-2 leading-relaxed animate-in fade-in slide-in-from-bottom-1 duration-300">
                  {i === lines.length - 1 ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" strokeWidth={2} /> : <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">{i + 1}</span>}
                  <span>{l}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LpGo({ lang = "es" }: { lang?: Lang }) {
  const c = COPY[lang];
  const navigate = useNavigate();
  const { isAuthenticated, isReady } = useAuthState();
  const canLoadOneTap = useDeferredMount(Infinity);
  const [authOpen, setAuthOpen] = useState(false);
  const [agentStarted, setAgentStarted] = useState(false);
  // Respaldo: cualquier consulta enviada desde la portada ensancha la vista.
  useEffect(() => {
    const on = () => setAgentStarted(true);
    window.addEventListener("open-support-chat", on);
    window.addEventListener("rl-new-consult-used", on);
    return () => { window.removeEventListener("open-support-chat", on); window.removeEventListener("rl-new-consult-used", on); };
  }, []);
  const planRailRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const rail = planRailRef.current;
    if (!rail || rail.scrollWidth <= rail.clientWidth) return;
    const el = rail.querySelector<HTMLElement>("[data-current='true']");
    if (el) rail.scrollLeft = el.offsetLeft - (rail.clientWidth - el.offsetWidth) / 2;
  }, []);
  const [authMode, setAuthMode] = useState<"signup" | "login">("signup");
  const dash = lang === "en" ? "/en/dashboard" : "/dashboard";

  const [annual, setAnnual] = useState(true);
  const startAuth = (plan: PlanKey, mode: "signup" | "login" = "signup") => {
    if (mode === "signup") {
      localStorage.setItem("pending_checkout", JSON.stringify({ product_type: "plan", product_key: plan, billing_interval: annual ? "year" : "month", is_paid_intent: true }));
    }
    if (isAuthenticated) { void navigate({ to: dash }); return; }
    setPostAuthIntent({ kind: "path", path: dash });
    setAuthMode(mode);
    setAuthOpen(true);
  };

  useEffect(() => {
    const on = () => startAuth("club_pro");
    const onPro = () => startAuth("club_pro");
    window.addEventListener(GO_TRIAL_EVENT, on);
    window.addEventListener(GO_CLUB_EVENT, onPro);
    return () => { window.removeEventListener(GO_TRIAL_EVENT, on); window.removeEventListener(GO_CLUB_EVENT, onPro); };
  });


  const x = EXTRA[lang];

  return (
    <div className="min-h-screen overflow-x-clip bg-card text-foreground">
      <Suspense fallback={null}>
        {authOpen && <LazyAuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} intent={{ kind: "path", path: dash }} />}
      </Suspense>

      {isReady && !isAuthenticated && canLoadOneTap && <Suspense fallback={null}><GoogleOneTap /></Suspense>}

      <main>
        <section className="pb-6 pt-0 [--hero-card:23rem] lg:[--hero-card:22rem]">
            <div className="min-w-0">
              <div data-started={agentStarted} className="group/hero relative overflow-hidden">
                <div className="md:hidden"><HeroPhoto bleed agent={c.agentName} labels={c.sectors} whispers={c.whispers} es={lang === "es"} /></div>
                <HeroSplit agent={c.agentName} labels={c.sectors} whispers={c.whispers} />
                <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-foreground/75 via-foreground/40 to-transparent px-5 pb-16 pt-16 md:from-foreground/70 md:via-foreground/30 md:pb-24 md:pt-16">
                  <div className="mb-5 hidden justify-center md:flex"><div className="rounded-2xl border border-border/50 bg-card/90 px-5 py-2 text-center shadow-lg backdrop-blur-md"><p className="font-display text-2xl font-extrabold tracking-tight text-foreground lg:text-3xl">RETAIL <span className="rounded-md bg-primary px-1.5 text-primary-foreground">LEMON</span></p><p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground">Managing Retail, Made Simple</p></div></div>
                  <h1 className="font-display mx-auto max-w-3xl text-center text-[1.9rem] font-extrabold leading-[1.08] tracking-tight text-background text-balance [text-shadow:0_2px_10px_rgb(0_0_0/0.35)] md:text-4xl lg:text-5xl">{c.h1}</h1>
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-[62%] bg-[linear-gradient(to_top,var(--card)_0%,var(--card)_10%,color-mix(in_oklab,var(--card)_85%,transparent)_24%,color-mix(in_oklab,var(--card)_60%,transparent)_40%,color-mix(in_oklab,var(--card)_32%,transparent)_58%,color-mix(in_oklab,var(--card)_12%,transparent)_78%,transparent_100%)] md:block" />
              </div>
              <div className={`relative z-10 -mt-[14%] px-4 transition-[max-width,margin] duration-700 ease-out md:mx-auto md:-mt-[var(--hero-card)] ${agentStarted ? "md:max-w-6xl" : "md:max-w-2xl"}`}>
                <div className="rounded-3xl border border-border/70 bg-card p-3 shadow-2xl">
                  <Suspense fallback={<div className="h-[420px] rounded-2xl border border-border bg-card" />}><PublicAgentConsult lang={lang} compact clubOnly onStart={() => setAgentStarted(true)} onJoinClub={() => window.dispatchEvent(new Event(GO_CLUB_EVENT))} /></Suspense>
                </div>
                <p className="mt-4 hidden text-center text-xs font-medium text-muted-foreground md:block">{c.notice}</p>
              </div>
            </div>
          </section>

        <div className="mt-6 px-4 pb-0 lg:mt-2 lg:pb-8"><SectorTicker lang={lang} /></div>

        



        <section id="que-incluye" className="scroll-mt-20 border-b border-border bg-band-slate pb-14 [container-type:inline-size] pt-10 lg:pt-16"><div className="mx-auto w-full max-w-6xl px-4">
          <p className="text-center text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{lang === "es" ? "Herramienta principal del Club Pro" : "The core Club Pro tool"}</p>
          <h2 className="font-display mx-auto mt-3 max-w-3xl text-center text-3xl font-extrabold leading-tight sm:text-4xl">
            {lang === "es" ? <>El Agente Retail IA, <span className="bg-primary px-1.5">el corazón</span> del Club Pro</> : <>The Retail AI Agent, <span className="bg-primary px-1.5">the heart</span> of Club Pro</>}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-[15px] text-muted-foreground">{lang === "es" ? "Tu copiloto para el día a día, conectado a todas las herramientas que necesitas para decidir y vender más." : "Your everyday co-pilot, connected to every tool you need to decide better and sell more."}</p>
          <div className="mt-10">
            <a href={lang === "en" ? "/en/retail-ai-agent" : "/agente-retail-ia"} className="group block rounded-3xl border-2 border-primary bg-card p-6 text-foreground shadow-sm transition-shadow hover:shadow-md sm:p-8">
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary"><MessageCircle className="h-7 w-7 text-primary-foreground" strokeWidth={1.5} /></span>
                <span className="flex-1"><span className="font-display block text-xl font-extrabold">{c.agentName}</span><span className="text-sm text-muted-foreground">{x.agentTile}</span></span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </div>
              <div className="mt-6 grid gap-5 border-t border-border pt-6 sm:grid-cols-3">
                {(lang === "es"
                  ? [["01", "Criterio de comercio real", "Respuestas en segundos, adaptadas a tu sector. Nada de teoría de consultor."], ["02", "Tu prioridad de la semana", "Te dice qué toca hoy para no perder el foco entre clientes y proveedores."], ["03", "Conectado a todo", "Te lleva a la herramienta justa: fugas, calculadora, subvenciones, KPIs…"]]
                  : [["01", "Real retail judgement", "Answers in seconds, adapted to your sector. No consultant theory."], ["02", "Your priority this week", "Tells you what matters today so you keep focus between customers and suppliers."], ["03", "Connected to everything", "Takes you to the right tool: leaks, calculator, grants, KPIs…"]]
                ).map(([n, t, d]) => (
                  <div key={n}>
                    <span className="font-display inline-block rounded-md bg-primary px-2 py-0.5 text-xs font-extrabold text-primary-foreground">{n}</span>
                    <p className="font-display mt-2 font-bold">{t}</p>
                    <p className="mt-1 text-sm leading-snug text-muted-foreground">{d}</p>
                  </div>
                ))}
              </div>
            </a>
            <div className="mx-auto mt-10 flex max-w-2xl flex-col items-center text-center">
              <h3 className="font-display tracking-tight">
                <span className="block text-[17px] font-semibold leading-snug text-foreground/70 min-[390px]:text-lg sm:text-2xl">{lang === "es" ? "El Agente Retail IA piensa contigo." : "The Retail AI Agent thinks with you."}</span>
                <span className="mt-1.5 block text-[22px] font-extrabold leading-tight text-foreground min-[390px]:text-2xl sm:text-4xl">{lang === "es" ? <>Nuestras <span className="rounded-md bg-primary px-1.5 text-primary-foreground">herramientas</span> te ayudan</> : <>Our <span className="rounded-md bg-primary px-1.5 text-primary-foreground">tools</span> help you</>}</span>
                <span className="mt-1 block text-[19px] font-bold leading-snug text-foreground/85 min-[390px]:text-xl sm:text-3xl">{lang === "es" ? "a conocer mejor tu negocio." : "know your business better."}</span>
              </h3>
            </div>
            <InfiniteRail>{[0, 1, 2].map((copy) => (<Fragment key={copy}>
                    {TOOL_TILES.map((t) => (
                      <div key={`${copy}-${t.key}`} className="w-40 shrink-0 sm:w-48">
                        <ToolFlip img={t.img} name={x.tools[t.key][0]} hint={x.tools[t.key][1]} back={TOOL_BACK[lang][t.key]} href={lang === "en" ? t.en : t.es} es={lang === "es"} free={"free" in t && t.free} />
                      </div>
                    ))}
                    <div aria-hidden={copy !== 1} className="flex w-40 shrink-0 flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-4 text-center sm:w-48">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary"><Sparkles className="h-5 w-5 text-primary-foreground" strokeWidth={1.5} /></span>
                      <p className="font-display mt-3 font-bold">{lang === "es" ? "Seguimos trabajando" : "We keep building"}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{lang === "es" ? "Nuevas herramientas cada mes" : "New tools every month"}</p>
                    </div>
              </Fragment>))}</InfiniteRail>
          </div>
        </div></section>

        <section id="planes" className="scroll-mt-20 bg-card pb-16 pt-14"><div className="mx-auto w-full max-w-6xl px-4">
          <p className="text-center text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{lang === "es" ? "Tarifas" : "Pricing"}</p>
          <h2 className="font-display mx-auto mt-3 text-center text-3xl font-extrabold leading-tight sm:text-4xl">{lang === "es" ? <>Elige <span className="bg-primary px-1.5">tu plan</span></> : <>Choose <span className="bg-primary px-1.5">your plan</span></>}</h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-[15px] text-muted-foreground">{c.notice}</p>
          <div role="radiogroup" aria-label={lang === "es" ? "Facturación" : "Billing"} className="mx-auto mt-6 flex w-max rounded-full border border-border bg-muted p-1 text-sm font-semibold">
            {[false, true].map((a) => (
              <button key={String(a)} type="button" role="radio" aria-checked={annual === a} onClick={() => setAnnual(a)} className={`min-h-[44px] rounded-full px-5 transition-colors ${annual === a ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
                {a ? (lang === "es" ? "Anual · 2 meses gratis" : "Annual · 2 months free") : (lang === "es" ? "Mensual" : "Monthly")}
              </button>
            ))}
          </div>
          <div ref={planRailRef} aria-label={c.pricesTitle} className="-mx-4 mt-8 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 pt-4 [scrollbar-width:none] md:mx-0 md:grid md:grid-cols-3 md:items-center md:gap-5 md:overflow-visible md:px-0 md:py-6 [&::-webkit-scrollbar]:hidden">
            {(["basico", "club_pro", "completo"] as PlanKey[]).map((k) => {
              const hot = k === "club_pro";
              return (
                <article key={k} data-current={hot} className={`relative flex w-[82%] shrink-0 snap-center flex-col rounded-2xl bg-card p-5 md:w-auto md:p-7 ${hot ? "border-2 border-primary shadow-xl md:z-10 md:scale-[1.06] md:py-9" : "border border-border shadow-sm"}`}>
                  {hot && <span className="absolute -top-3 left-5 whitespace-nowrap rounded-full bg-primary px-3 py-1 text-[10px] font-bold tracking-wide text-primary-foreground">{c.popular}</span>}
                  <h3 className={`font-display font-bold ${hot ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"}`}>{c.names[k]}</h3>
                  <p className="text-sm text-muted-foreground">{c.plans[k]}</p>
                  <p className="mt-3">
                    {annual && <span className="mr-2 text-base text-muted-foreground line-through tabular-nums">{PRICES[k]} €</span>}
                    <span className={`font-display font-extrabold tabular-nums ${hot ? "text-4xl md:text-5xl" : "text-3xl md:text-4xl"}`}>{annual ? annualMonthly(PRICES[k], lang) : PRICES[k]} €</span><span className="text-sm text-muted-foreground">{c.month}</span>
                  </p>
                  <ul className="mt-4 space-y-2 text-sm md:text-[15px]">
                    {FEATURES[lang][k].map((f) => (<li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />{f}</li>))}
                  </ul>
                  <div className="mt-auto pt-5">
                    <button type="button" onClick={() => startAuth(k)} className={`min-h-[48px] w-full rounded-full text-sm font-bold ${hot ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-border hover:bg-muted"}`}>{c.trial}</button>
                  </div>
                </article>
              );
            })}
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            <a href={lang === "en" ? "/en/pricing" : "/tarifas"} className="inline-flex min-h-[44px] items-center gap-1 underline underline-offset-4 hover:text-foreground">
              {lang === "es" ? "Ver comparativa completa de tarifas" : "See full pricing comparison"}
              <ArrowRight className="h-3 w-3" />
            </a>
          </p>
        </div></section>

        <section className="border-t border-border bg-band-slate py-16"><div className="mx-auto max-w-2xl px-4">
          <h2 className="font-display text-center text-2xl font-extrabold sm:text-3xl">{c.faqTitle}</h2>
          <Accordion type="single" collapsible className="mt-8 space-y-3">
            {c.faq.map(([q, a], i) => (
              <AccordionItem key={q} value={`f${i}`} className="rounded-2xl border border-border bg-card px-5 shadow-sm">
                <AccordionTrigger className="min-h-[44px] text-left">{q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div></section>
      </main>
      <HomeCorporateFooter dark />
    </div>
  );
}
